import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  isCalendarPath,
  MOBILE_CALENDAR_STATE_EVENT,
  MOBILE_CALENDAR_TOGGLE_EVENT,
  MOBILE_NAVIGATION_OPEN_EVENT,
  MOBILE_OVERLAYS_CLOSE_EVENT,
} from '../utils/mobileNavigation';

const MOBILE_BREAKPOINT = 768;

const Dock = styled.nav`
  display: none;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1080;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    min-height: 62px;
    padding-bottom: env(safe-area-inset-bottom);
    border-top: 1px solid var(--border);
    background: color-mix(in srgb, var(--surface) 97%, var(--primary) 3%);
    box-shadow: 0 -10px 28px rgba(0, 0, 0, 0.25);

    a,
    button {
      display: flex;
      min-width: 0;
      min-height: 58px;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.15rem;
      padding: 0.25rem;
      border: 0;
      background: transparent;
      color: var(--text-secondary);
      cursor: pointer;
      font: inherit;
      font-size: 0.66rem;
      font-weight: 700;
      text-decoration: none;
    }

    a[aria-current='page'],
    button[aria-pressed='true'] {
      color: var(--primary);
    }

    a:hover,
    a:focus-visible,
    button:hover,
    button:focus-visible {
      background: color-mix(in srgb, var(--primary) 9%, transparent);
      color: var(--primary);
    }
  }
`;

const Icon = styled.span`
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;

  svg {
    width: 22px;
    height: 22px;
  }
`;

const ShowsIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M4 5h16v5a2.5 2.5 0 0 0 0 5v4H4v-4a2.5 2.5 0 0 0 0-5z" />
    <path d="M12 7v10" strokeDasharray="2 2" />
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M8 3v4M16 3v4M3 10h18M8 14h2M14 14h2M8 18h2" />
  </svg>
);

const BookmarkIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M6.5 4.5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v16l-5.5-3.8-5.5 3.8z" />
  </svg>
);

const MoreIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
    <circle cx="5" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="19" cy="12" r="1.7" />
  </svg>
);

const closeTransientOverlays = () => {
  window.dispatchEvent(new window.Event(MOBILE_OVERLAYS_CLOSE_EVENT));
};

const MobileAppNavigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [calendarExpanded, setCalendarExpanded] = useState(false);
  const onCalendarRoute = isCalendarPath(location.pathname);

  useEffect(() => {
    setCalendarExpanded(false);
  }, [location.pathname]);

  useEffect(() => {
    const syncCalendarState = (event: Event) => {
      const detail = (event as CustomEvent<{ open?: boolean }>).detail;
      setCalendarExpanded(Boolean(detail?.open));
    };

    window.addEventListener(MOBILE_CALENDAR_STATE_EVENT, syncCalendarState);
    return () => window.removeEventListener(MOBILE_CALENDAR_STATE_EVENT, syncCalendarState);
  }, []);

  const openCalendar = () => {
    closeTransientOverlays();
    // The close event already dismisses an expanded drawer. Sending a toggle
    // in the same React event would batch false then true and reopen it.
    if (calendarExpanded) return;
    if (onCalendarRoute) {
      window.dispatchEvent(new window.Event(MOBILE_CALENDAR_TOGGLE_EVENT));
      return;
    }
    navigate('/?calendar=month');
  };

  const openMore = () => {
    closeTransientOverlays();
    window.dispatchEvent(new window.Event(MOBILE_NAVIGATION_OPEN_EVENT));
  };

  return (
    <Dock aria-label="Mobile app navigation">
      <Link
        to="/"
        aria-current={location.pathname === '/' && !calendarExpanded ? 'page' : undefined}
        onClick={closeTransientOverlays}
      >
        <Icon><ShowsIcon /></Icon>
        Shows
      </Link>
      <button
        type="button"
        aria-label="Open month calendar"
        aria-expanded={calendarExpanded}
        aria-pressed={calendarExpanded}
        onClick={openCalendar}
      >
        <Icon><CalendarIcon /></Icon>
        Calendar
      </button>
      <Link
        to="/saved-events"
        aria-current={location.pathname === '/saved-events' ? 'page' : undefined}
        onClick={closeTransientOverlays}
      >
        <Icon><BookmarkIcon /></Icon>
        Saved
      </Link>
      <button type="button" aria-label="Open navigation menu" aria-pressed="false" onClick={openMore}>
        <Icon><MoreIcon /></Icon>
        More
      </button>
    </Dock>
  );
};

export default MobileAppNavigation;
